import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Link2, FileUp, Trash2, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/landing`;

const Swatches = ({ d }) => (
  <div className="flex gap-1">
    {["accent", "dark", "bg", "surface"].map((k) => d?.[k] && <span key={k} title={k} className="h-4 w-4 rounded-full border border-zinc-300" style={{ background: d[k] }} />)}
  </div>
);

const ModelRow = ({ m, onApply, onDelete, busy }) => (
  <div className="flex items-start gap-3 rounded-lg border border-zinc-200 p-2.5" data-testid={`lp-model-${m.id}`}>
    <div className="min-w-0 flex-1 space-y-1">
      <div className="flex items-center gap-2"><p className="truncate text-sm font-medium text-zinc-900">{m.name}</p><Swatches d={m.design} /></div>
      {m.summary && <p className="text-[11px] text-zinc-600">{m.summary}</p>}
      <p className="truncate text-[10px] text-zinc-500">{m.source?.type === "pdf" ? `PDF: ${m.source.filename}` : m.source?.url} · {m.design?.heading_font || "font temă"}</p>
    </div>
    {m.status === "running" && <span className="flex items-center gap-1 text-[11px] text-amber-600" data-testid={`lp-model-running-${m.id}`}><Loader2 className="h-3.5 w-3.5 animate-spin" /> AI-ul citește modelul…</span>}
    {m.status === "failed" && <span className="max-w-[160px] text-[11px] text-rose-600" data-testid={`lp-model-failed-${m.id}`}>Eșuat: {m.error}</span>}
    {m.status !== "running" && m.status !== "failed" && <button data-testid={`lp-model-apply-${m.id}`} onClick={() => onApply(m)} disabled={!!busy} className="flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1 text-xs font-semibold text-zinc-950 disabled:opacity-50">
      {busy === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Aplică
    </button>}
    <button data-testid={`lp-model-delete-${m.id}`} onClick={() => onDelete(m)} className="p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
  </div>
);

export const ModelDialog = ({ d, text, beforeSend, onClose, onDone }) => {
  const [items, setItems] = useState(null);
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const load = () => axios.get(`${API}/models`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const running = items?.some((m) => m.status === "running");
  useEffect(() => { if (!running) return; const t = setInterval(load, 4000); return () => clearInterval(t); }, [running]);

  const add = async (key, req) => {
    setBusy(key);
    try { const r = await req(); toast.success("Model adăugat — AI-ul îl citește (~1 minut)"); setUrl(""); setName(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message, { duration: 10000 }); }
    setBusy("");
  };
  const fromUrl = () => url.trim() && add("url", () => axios.post(`${API}/models/url`, { url: url.trim(), name }, { timeout: 30000 }));
  const fromPdf = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("name", name);
    add("pdf", () => axios.post(`${API}/models/pdf`, fd, { timeout: 120000 }));
  };
  const apply = async (m) => {
    setBusy(m.id);
    try { await beforeSend(); const r = await axios.post(`${API}/${d.id}/apply-model`, { model_id: m.id, text }, { timeout: 180000 }); onDone(r.data); toast.success("Model aplicat — vezi previzualizarea"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };
  const del = async (m) => { if (!window.confirm(`Ștergi modelul „${m.name}”?`)) return; await axios.delete(`${API}/models/${m.id}`); load(); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lp-model-dialog" className="max-w-xl">
        <DialogHeader><DialogTitle>Modele de landing page</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">Preia de la model ordinea secțiunilor, culorile, fonturile, aranjarea și stilul textelor. Textele și pozele rămân ale produsului tău.{text ? " Mesajul scris în chat se trimite ca indicație." : ""}</p>
        <div className="space-y-2 rounded-lg border border-zinc-200 p-3">
          <Input data-testid="lp-model-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nume model (opțional)" className="h-8 text-xs" />
          <div className="flex gap-2">
            <Input data-testid="lp-model-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… link spre pagina model" className="h-8 text-xs" />
            <button data-testid="lp-model-url-btn" onClick={fromUrl} disabled={!!busy || !url.trim()} className="flex shrink-0 items-center gap-1 rounded-full border border-amber-500 px-3 text-xs text-zinc-800 hover:bg-amber-400 hover:text-zinc-950 disabled:opacity-40">
              {busy === "url" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Adaugă din link
            </button>
            <button data-testid="lp-model-pdf-btn" onClick={() => fileRef.current?.click()} disabled={!!busy} className="flex shrink-0 items-center gap-1 rounded-full border border-zinc-300 px-3 text-xs text-zinc-700 hover:border-amber-500 disabled:opacity-40">
              {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />} PDF
            </button>
            <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={fromPdf} data-testid="lp-model-pdf-input" />
          </div>
        </div>
        <div className="max-h-80 space-y-2 overflow-y-auto" data-testid="lp-model-list">
          {items === null ? <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
            : items.length === 0 ? <p className="text-xs text-zinc-500" data-testid="lp-model-empty">Niciun model salvat încă. Adaugă unul din link sau PDF.</p>
            : items.map((m) => <ModelRow key={m.id} m={m} busy={busy} onApply={apply} onDelete={del} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
